import { createRouter, createWebHistory } from "vue-router";
import { landingPageRedirects } from "./redirects.js";

const routes = [
  // Login
  {
    component: () => import("../components/WelcomePage.vue"),
    name: "login",
    path: "/login",
    meta: { public: true },
  },

  // Legacy landing page redirects
  ...landingPageRedirects,
  {
    path: "/events/:slug/flights",
    name: "lander_flights_lookup",
    component: () => import("../components/LanderFlightsLookup.vue"),
    props: true,
    meta: {
      isLander: true,
    },
  },
  {
    path: "/events/:slug",
    name: "lander",
    component: () => import("../components/LanderPage.vue"),
    props: true,
    meta: {
      public: true,
      isLander: true,
    },
  },

  // App
  {
    component: () => import("../components/HomeView.vue"),
    name: "home",
    path: "/",
  },
  {
    component: () => import("../components/TripView.vue"),
    name: "trip",
    path: "/trips/:tripId",
    beforeEnter: (to, from, next) => {
      const rawId = to.params.tripId;
      const correctedId = rawId.match(/^\d+/)?.[0];
      if (correctedId && correctedId !== rawId) {
        next({ name: "trip", params: { tripId: correctedId } });
      } else {
        next();
      }
    },
  },
  {
    path: "/trips/:tripId/flights",
    redirect: to => `/trips/${to.params.tripId}`,
  },
  {
    component: () => import("../components/TripViewApprove.vue"),
    name: "trip_approve",
    path: "/trips/:tripId/approve",
  },
  {
    component: () => import("../components/TripNew.vue"),
    name: "trip_new",
    path: "/trips/new",
  },
  {
    component: () => import("../components/TripView.vue"),
    name: "trip_receipt",
    path: "/trips/:tripId/receipt",
  },
  {
    component: () => import("../components/TripView.vue"),
    name: "trip_itinerary",
    path: "/trips/:tripId/itineraries/:itineraryIndex",
  },
  {
    component: () => import("../components/DashboardView.vue"),
    path: "/dashboard",
    redirect: { name: "trips" },
    children: [
      {
        component: () => import("../components/TripList.vue"),
        name: "trips",
        path: "/trips",
        alias: ["/dashboard"],
      },
      {
        component: () => import("../components/ReportsView.vue"),
        path: "/reports",
        children: [
          {
            component: () => import("../components/ReportsCustom.vue"),
            name: "reports_custom",
            path: "/reports/custom/:reportId",
          },
          {
            component: () => import("../components/ReportsCustomUsers.vue"),
            name: "reports_custom_users",
            path: "/reports/custom/1",
          },
          {
            component: () => import("../components/ReportsCustomFlights.vue"),
            name: "reports_custom_flights",
            path: "/reports/custom/4",
          },
          {
            component: () => import("../components/ReportsCustomVolume.vue"),
            name: "reports_custom_volume",
            path: "/reports/custom/5",
          },
          {
            component: () => import("../components/ReportsFlightsManifest.vue"),
            name: "reports_flights_manifest",
            path: "/reports/flights/manifest",
            alias: ["/reports"],
          },
          {
            component: () => import("../components/ReportsFlightsManifest.vue"),
            name: "reports_student_flights",
            path: "/reports/student/flights",
          },
          {
            component: () => import("../components/ReportsFlightsVolume.vue"),
            name: "reports_flight_volume",
            path: "/reports/flight/volume",
          },
          {
            component: () => import("../components/ReportsTransactions.vue"),
            name: "reports_transactions",
            path: "/reports/transactions",
          },
          {
            component: () => import("../components/ReportsTravelersCosts.vue"),
            name: "reports_travelers_costs",
            path: "/reports/travelers/costs",
          },
          {
            component: () => import("../components/ReportsTravelersFlights.vue"),
            name: "reports_travelers_flights",
            path: "/reports/travelers/flights",
          },
          {
            component: () => import("../components/ReportsTravelersHotels.vue"),
            name: "reports_travelers_hotels",
            path: "/reports/travelers/hotels",
          },
        ],
      },
      {
        component: () => import("../components/SettingsView.vue"),
        path: "/settings",
        props: true,
        children: [
          {
            component: () => import("../components/SettingsAccount.vue"),
            name: "settings_account",
            path: "/settings/account",
            alias: ["/settings"],
          },
          { path: "/settings/customer/account", redirect: "/settings/account" },
          { path: "/settings/organization/account", redirect: "/settings/account" },
          {
            component: () => import("../components/SettingsTravelers.vue"),
            name: "settings_travelers",
            path: "/settings/travelers",
          },
          { path: "/settings/customer/travelers", redirect: "/settings/travelers" },
          { path: "/settings/organization/travelers", redirect: "/settings/travelers" },
          {
            component: () => import("../components/SettingsBilling.vue"),
            name: "settings_billing",
            path: "/settings/billing",
          },
          { path: "/settings/customer/cards", redirect: "/settings/billing" },
          { path: "/settings/organization/cards", redirect: "/settings/billing" },
          {
            component: () => import("../components/SettingsPolicies.vue"),
            name: "settings_policies",
            path: "/settings/policies",
          },
          { path: "/settings/organization/policies", redirect: "/settings/policies" },

          {
            component: () => import("../components/SettingsTeam.vue"),
            name: "settings_team",
            path: "/settings/team",
            props: true,
          },
          { path: "/settings/organization/users", redirect: "/settings/team" },
          {
            component: () => import("../components/SettingsVouchers.vue"),
            name: "settings_vouchers",
            path: "/settings/vouchers",
          },
        ],
      },
    ],
  },
  {
    path: "/:catchAll(.*)",
    name: "none",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Handle navigation errors (including dynamic import failures)
router.onError(error => {
  // If chunk loading fails, reload the page to get fresh assets
  if (error.message && (error.message.includes("Failed to fetch") || error.message.includes("Loading chunk"))) {
    window.location.reload();
  } else {
    console.error("Router error:", error);
  }
});

const getAllRoutes = routes => {
  return routes.flatMap(route => {
    if (route.children) {
      return [route, ...getAllRoutes(route.children)];
    }
    return route;
  });
};

const publicRoutes = getAllRoutes(routes).filter(route => route.meta?.public);

export const isPublicRoute = currentPath => {
  return publicRoutes.some(route => {
    // Exact match
    if (route.path === currentPath) return true;

    // Dynamic route match
    if (route.path.includes(":")) {
      // Convert Vue route pattern to regex
      const pattern = route.path
        .replace(/:[^/]+/g, "[^/]+") // Replace :param with regex pattern
        .replace(/\//g, "\\/"); // Escape forward slashes
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(currentPath);
    }

    return false;
  });
};

export default router;
